import {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';

// Manually maintained list of pathnames used by the tabs that should be filtered out. Might be possible to make an automatic solution, but currently was the best I could come up with.
const tabPaths = ['/period', '/month-by-month'];

const removeTabpathsFromPathname = (pathname: string) => {
	return tabPaths.reduce((pathname, tabPath) => {
		return pathname ? pathname.replace(tabPath, '') : '';
	}, pathname);
};

export type UseRouterPromptProps = {
	hasChanges: boolean;
	onSave: (onSuccess: () => void) => void;
	onSaveAs?: (onSuccess: () => void) => void;
	onDiscard?: () => Promise<boolean>; // optional handler to prevent discarding changes by returning false
	ignoreChildPaths?: boolean;
};
type UseRouterPromptReturn = {
	showPrompt: boolean;
	handleSave: () => void;
	handleSaveAs: () => void;
	handleDiscard: () => void;
	closePrompt: () => void;
};

const handleBeforeUnload = (e?: BeforeUnloadEvent) => {
	if (e) {
		e.preventDefault();
		e.returnValue = 'Do you want to save your changes?'; // cannot use intl here - this has to be a plain js function, besides, most browsers show their own prompts
	}
};

export const useRouterPrompt = ({
	hasChanges,
	onSave,
	onSaveAs,
	onDiscard,
	ignoreChildPaths,
}: UseRouterPromptProps): UseRouterPromptReturn => {
	const history = useHistory();
	const {location} = history;

	const [showPrompt, setShowPrompt] = useState(false);
	const [currentPath, setCurrentPath] = useState('');

	const removeBlocker = useCallback(() => {
		window.removeEventListener('beforeunload', handleBeforeUnload);
		history.block(() => void 0);
	}, [history]);

	useEffect(() => {
		if (hasChanges) {
			// handles when page is unloaded (refresh, close, click favourite, etc)
			// it uses the browser default prompt
			window.addEventListener('beforeunload', handleBeforeUnload);
			// internal routing allows a custom prompt to be shown
			history.block(prompt => {
				const currentPathnameWithoutTabSuffix = removeTabpathsFromPathname(location?.pathname ?? ''); // Seems like location can be undefined in the tests since there is no parent <Router />. Not sure how to circumvent.
				const skip = ignoreChildPaths && prompt.pathname.includes(currentPathnameWithoutTabSuffix);
				if (!skip) {
					setCurrentPath(prompt.pathname);
					setShowPrompt(true);
					return 'true';
				}
			});
		} else {
			removeBlocker();
		}

		return () => {
			removeBlocker();
		};
	}, [history, hasChanges]);

	const handleSave = useCallback(async () => {
		if (onSave) {
			const onSuccess = () => {
				removeBlocker();
				history.push(currentPath);
			};
			onSave(onSuccess);
		}
	}, [currentPath, history, onSave]);

	const handleSaveAs = useCallback(async () => {
		if (onSaveAs) {
			const onSuccess = () => {
				removeBlocker();
				history.push(currentPath);
			};
			onSaveAs(onSuccess);
		}
	}, [currentPath, history, onSaveAs]);

	const handleDiscard = useCallback(async () => {
		const canRoute = onDiscard ? await Promise.resolve(onDiscard()) : true;
		if (canRoute) {
			removeBlocker();
			history.push(currentPath);
			setShowPrompt(false);
		}
	}, [currentPath, history, onDiscard]);

	const closePrompt = useCallback(() => {
		setShowPrompt(false);
	}, []);

	return {showPrompt, handleSave, handleSaveAs, handleDiscard, closePrompt};
};
